import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import Sparkle from "@/components/Sparkle";
import { scrollToId } from "@/components/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1781004901688-2a7982309135?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxtb2JpbGUlMjBiYXIlMjBldmVudCUyMG91dGRvb3JzfGVufDB8fHx8MTc5MDcxMjc0MHww&ixlib=rb-4.1.0&q=85";

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="grain relative min-h-screen overflow-hidden bg-espresso-deep">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Mobile bar event"
          className="h-full w-full object-cover"
          data-testid="hero-background-image"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-espresso-deep/80 via-espresso-deep/55 to-espresso-deep" />
        <div className="absolute inset-0 bg-gradient-to-r from-espresso-deep/70 via-transparent to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-end px-5 pb-20 pt-32 sm:px-8 lg:pb-24"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-6 flex items-center gap-3"
        >
          <Sparkle className="animate-twinkle h-4 w-4 text-gold" />
          <span className="eyebrow text-gold">Dry hire mobile bar — La Cantera to Boerne, TX</span>
        </motion.div>

        <h1 className="font-display text-[13.5vw] leading-[0.95] text-cream sm:text-7xl lg:text-8xl xl:text-[7.5rem]">
          <MaskedLine delay={0.25}>We bring the bar,</MaskedLine>
          <MaskedLine delay={0.45}>
            <span>
              you bring the <em className="italic text-rose">party.</em>
            </span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-cream/75 sm:text-lg"
        >
          Full-service mobile bartending for weddings, birthdays and backyard bashes —
          mixers, garnish, glassware and good times included. You supply the crowd,
          we shake up the rest.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-book"
            onClick={() => scrollToId("book")}
            className="rounded-full bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-espresso transition-all duration-300 hover:scale-[1.03] hover:bg-cream"
          >
            Book your date
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="pointer-events-none absolute -top-4 right-6 hidden w-[clamp(160px,18vw,280px)] sm:block lg:right-16"
        >
          <img
            src="/logo-glass.png"
            alt="Shaken Society coupe glass"
            className="animate-floaty w-full drop-shadow-[0_20px_40px_rgba(0,0,0,0.45)]"
            data-testid="hero-glass-emblem"
          />
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-hint"
        onClick={() => scrollToId("about")}
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 text-cream/60 transition-colors hover:text-gold md:block"
        aria-label="Scroll down"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.button>
    </section>
  );
}